import { CalendarOutlined, CheckSquareOutlined, CloseCircleFilled, DownOutlined, FieldTimeOutlined, FilterFilled } from "@ant-design/icons";
import { Button, Dropdown, Menu, Select, Space, Tag } from "antd";
import useAvailablePackageTypes from "hooks/useAvailablePackageTypes";
import { ListingDuration, ListingDurationNames } from "models/pendingListings";
import { PendingUploadExtras } from "pages/staff/PendingListings";
import { FC, useState } from "react";
import ChangeUploadDateModal from "./ChangeUploadDateModal";
import MoreListingsTonightModal from "./MoreListingsTonightModal";

const { Option } = Select;

export enum BulkType {
    Selection = 0,
    Filter = 1
}

interface PendingListingsToolbarProps {
    selectedRowCount: number;
    filteredRowCount: number;
    onChangedCardUploadDates?: (uploadDate: string | null, type: BulkType) => Promise<void>;
    onChangedPackageUploadDates?: (uploadDate: string, packageIds: string[]) => Promise<void>;
    onDurationChanged?: (newDuration: ListingDuration, type: BulkType) => Promise<void>;
    onFilterClear?: () => void;
    onPackageTypeChanged?: (packageTypeId: string | null) => void;
    packageTypeId: string | null;
    pendingUploadExtra?: PendingUploadExtras;
    friendlyIdFilter: string | undefined;
}

const DurationOptions = [
    ListingDuration.Days_1,
    ListingDuration.Days_3,
    ListingDuration.Days_5,
    ListingDuration.Days_7,
    ListingDuration.Days_10
].map(key => ({
    label: ListingDurationNames[key],
    key,
}));

const resolveBulkType = (type: BulkType, selectionCount: number, filterCount: number) => {
    switch (type) {
        case BulkType.Selection:
            return {
                icon: <CheckSquareOutlined />,
                affectedCount: selectionCount,
                affectedString: `${selectionCount === 0 ? 'No' : selectionCount} Row${selectionCount !== 1 ? 's' : ''} Selected`,
                valid: selectionCount > 0
            };

        case BulkType.Filter:
            return {
                icon: <FilterFilled />,
                affectedCount: filterCount,
                affectedString: `${filterCount === 0 ? 'No' : filterCount} Row${filterCount !== 1 ? 's' : ''} Filtered`,
                valid: filterCount > 0
            };

        default:
            return {
                icon: <CloseCircleFilled />,
                affectedCount: 0,
                affectedString: 'Invalid',
                valid: false
            };
    }
}

const PendingListingsToolbar: FC<PendingListingsToolbarProps> = ({
    selectedRowCount,
    filteredRowCount,
    onChangedCardUploadDates,
    onChangedPackageUploadDates,
    onDurationChanged,
    onFilterClear,
    onPackageTypeChanged,
    packageTypeId,
    pendingUploadExtra,
    friendlyIdFilter
}) => {
    const { data: packageTypes = [] } = useAvailablePackageTypes();
    const [uploadDateModalOpen, setUploadDateModalOpen] = useState(false);
    const [listingsModalOpen, setListingsModalOpen] = useState(false);
    const [bulkType, setBulkType] = useState(BulkType.Selection);
    const { icon, affectedCount, affectedString, valid } = resolveBulkType(bulkType, selectedRowCount, filteredRowCount);

    const { uploadCountTonight = 0, enqueuedCount = 0, pendingCount = 0 } = pendingUploadExtra ?? {};

    return <>
        <Space size={20}>
            <Dropdown.Button
                icon={<DownOutlined />}
                type={valid ? 'primary' : 'default'}
                onClick={() => setBulkType(cur => cur === 0 ? 1 : 0)}
                overlay={<Menu mode="vertical" items={[
                    {
                        label: 'Change Upload Date',
                        icon: <CalendarOutlined />,
                        key: 'change-upload-date',
                        disabled: !valid,
                        onClick: () => setUploadDateModalOpen(true)
                    },
                    {
                        label: 'Change Duration',
                        icon: <FieldTimeOutlined />,
                        key: 'change-duration',
                        disabled: !valid,
                        children: [...DurationOptions.map(x => ({
                            ...x,
                            onClick: async () => onDurationChanged && await onDurationChanged(x.key, bulkType)
                        }))]
                    }
                ]} />}
            >
                {icon} {affectedString}
            </Dropdown.Button>

            <Select
                style={{ minWidth: '180px' }}
                value={packageTypeId ?? ""}
                onChange={(id: string) => {
                    if (!onPackageTypeChanged) {
                        return;
                    }

                    const packageTypeId = id.trim();

                    if (packageTypeId === "") {
                        onPackageTypeChanged(null);
                    } else {
                        onPackageTypeChanged(packageTypeId);
                    }
                }}
            >
                <Option value="">- All Package Types -</Option>
                {packageTypes.map(pt => (
                    <Option key={pt.id} value={pt.id}>{pt.internalName}</Option>
                ))}
            </Select>

            {pendingUploadExtra !== undefined && friendlyIdFilter === undefined &&
                <Button type="link" onClick={() => setListingsModalOpen(true)}>
                    {uploadCountTonight.toLocaleString()} listings going live tonight.
                    {enqueuedCount.toLocaleString()} pending listings are currently uploading,
                    and {pendingCount.toLocaleString()} are available to be added to tonight's upload.
                </Button>}

            {friendlyIdFilter && <div id="staff-card-active-filters">
                <Tag
                    closable
                    color="blue"
                    onClose={onFilterClear}
                >
                    Package ID: {friendlyIdFilter}
                </Tag>
            </div>}
        </Space>

        <ChangeUploadDateModal
            open={uploadDateModalOpen}
            affectedCount={affectedCount}
            onCancel={() => setUploadDateModalOpen(false)}
            onConfirm={async (date) => {
                if (onChangedCardUploadDates) {
                    await onChangedCardUploadDates(date, bulkType);
                }
                setUploadDateModalOpen(false);
            }}
        />

        <MoreListingsTonightModal
            open={listingsModalOpen}
            onCancel={() => setListingsModalOpen(false)}
            onConfirm={async (uploadDate, packageIds) => {
                if (onChangedPackageUploadDates) {
                    await onChangedPackageUploadDates(uploadDate, packageIds);
                }
                setListingsModalOpen(false);
            }}
            currentUploadCount={uploadCountTonight ?? 0}
        />
    </>;
};

export default PendingListingsToolbar;